// src/utils/passkeys.js
// 3.69.0 (owner, Oct 29): Face ID / fingerprint unlock. After an hour
// without use the portal locks instead of signing out for good, and the
// person opens it again with Face ID, Touch ID, their fingerprint or
// Windows Hello (a passkey kept on the device; the server side is
// routes/mfa.js /passkey/...). Here: what this browser keeps, the word for
// this device's unlock, and the calls to the browser and the server.
//
// This browser keeps, per email: { id, name, validUntil, paused }.
// paused: they signed out themselves before 3.69.2, so the password comes
// first (a password sign-in here un-pauses it and renews the 30 days).
// 3.69.2 (owner): signing out no longer pauses it; the sign-in page offers
// it instead ("Sign in with Touch ID"), within the same 30 days.
import { startRegistration, startAuthentication, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';
import { API_BASE } from '../firebase/config';

const KEY       = 'faxPortal.passkeys';
export const LOCK_KEY = 'faxPortal.locked';   // the email the portal locked for
const LOCK_WHY_KEY = 'faxPortal.lockedWhy';    // 'idle' (an hour without use) or 'signout' (3.69.2)
const OFFER_KEY = 'faxPortal.passkeyOffer';   // { [email]: when "Not now" was picked }
const OFFER_AGAIN_DAYS = 30;

const low = email => String(email || '').trim().toLowerCase();
function readAll(key) {
  try { return JSON.parse(window.localStorage.getItem(key) || '{}') || {}; } catch { return {}; }
}
function writeAll(key, map) {
  try { window.localStorage.setItem(key, JSON.stringify(map)); } catch { /* not kept */ }
}

// This device's passkey for them (expired ones too: a password sign-in renews them)
export function passkeyFor(email) { return readAll(KEY)[low(email)] || null; }
export function savePasskey(email, entry) {
  const map = readAll(KEY);
  map[low(email)] = { ...(map[low(email)] || {}), ...entry };
  writeAll(KEY, map);
}
export function forgetPasskey(email) {
  const map = readAll(KEY);
  delete map[low(email)];
  writeAll(KEY, map);
  if (lockedEmail() === low(email)) clearLock();
}
// Usable for unlocking right now (not paused, within its 30 days)
export function passkeyReady(email) {
  const p = passkeyFor(email);
  return !!p && !p.paused && Date.parse(p.validUntil) > Date.now();
}

// ── The lock ─────────────────────────────────────────────────
function lockedEmail() {
  try { return window.localStorage.getItem(LOCK_KEY) || ''; } catch { return ''; }
}
// The portal locks (instead of a plain sign-out) only when this device can
// unlock. why: 'idle' after an hour without use; 'signout' when they signed
// out themselves or typed an email that has it here (3.69.2)
export function lockFor(email, why = 'idle') {
  if (!passkeyReady(email)) return false;
  try {
    window.localStorage.setItem(LOCK_WHY_KEY, why);
    window.localStorage.setItem(LOCK_KEY, low(email));
  } catch { return false; }
  return true;
}
export function clearLock() {
  try { window.localStorage.removeItem(LOCK_KEY); window.localStorage.removeItem(LOCK_WHY_KEY); } catch { /* nothing kept */ }
}
// { email, id, name, why } while locked, else null
export function currentLock() {
  const email = lockedEmail();
  if (!email || !passkeyReady(email)) return null;
  const p = passkeyFor(email);
  let why = 'idle';
  try { why = window.localStorage.getItem(LOCK_WHY_KEY) === 'signout' ? 'signout' : 'idle'; } catch { /* idle */ }
  return { email, id: p.id, name: p.name || '', why };
}

// ── This device ──────────────────────────────────────────────
// The device's own name for it, for buttons and messages. 3.69.1 (owner):
// names the code too, because a device without Face ID or a fingerprint
// asks for its passcode, screen lock or password instead (the browser
// doesn't say which beforehand). Android face unlock is often too weak for
// this, so those phones ask for the fingerprint or screen lock.
export function unlockWord() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPod/.test(ua)) return 'Face ID or passcode';
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'Face ID, Touch ID or passcode';
  if (/Mac OS/.test(ua)) return 'Touch ID or Mac password';
  if (/Android/.test(ua)) return 'face, fingerprint or screen lock';
  if (/Windows/.test(ua)) return 'Windows Hello';
  return 'face, fingerprint or screen lock';
}
export function isInstalledApp() {
  try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true; } catch { return false; }
}

// Can this browser set one up here: WebAuthn, a built-in face or
// fingerprint reader, the server switched on, and the portal's own address
let availability = null;
export function passkeysPossible() {
  if (process.env.NODE_ENV === 'development' && window.__previewPasskeys) return Promise.resolve(true);   // layout preview only
  if (!availability) {
    availability = (async () => {
      try {
        if (!browserSupportsWebAuthn() || !(await platformAuthenticatorIsAvailable())) return false;
        const res = await fetch(`${API_BASE}/portal/mfa/passkey/available`);
        const j = res.ok ? await res.json() : {};
        return !!j.available && j.rpId === window.location.hostname;
      } catch { return false; }
    })();
  }
  return availability;
}

// "Not now" on the question after signing in: asked again after 30 days
export function offerDeclined(email) {
  const at = readAll(OFFER_KEY)[low(email)];
  return !!at && Date.now() - Date.parse(at) < OFFER_AGAIN_DAYS * 864e5;
}
export function declineOffer(email) {
  const map = readAll(OFFER_KEY);
  map[low(email)] = new Date().toISOString();
  writeAll(OFFER_KEY, map);
}

// ── Server calls ─────────────────────────────────────────────
async function call(path, { method = 'POST', body, getToken } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (getToken) headers.Authorization = `Bearer ${await getToken()}`;
  const res = await fetch(`${API_BASE}/portal/mfa${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(j.error || 'Something went wrong. Please try again.'), { code: j.code, status: res.status, kind: j.kind, from: j.from, until: j.until });
  return j;
}

// The browser said no: they cancelled, or the face / finger didn't match
const cancelled = e => e?.name === 'NotAllowedError' || e?.name === 'AbortError';
function deviceError(e, unlocking = false) {
  // 3.69.3: when unlocking, the browser says the same when the passkey isn't
  // on the device any more (removed or replaced), so the message covers both
  if (cancelled(e) && !unlocking) return Object.assign(new Error('It was cancelled.'), { code: 'CANCELLED' });
  if (cancelled(e)) return Object.assign(new Error("Unlock didn't finish. If your device says it has no passkey for this site, it no longer has its sign-in: please use your password, then turn it on again in Security."), { code: 'CANCELLED' });
  return Object.assign(new Error("That didn't work on this device. Please try again, or sign in with your password."), { code: 'DEVICE' });
}

// Turn it on for this device (needs a password sign-in in the last 10
// minutes: REAUTH_REQUIRED otherwise). Returns { id, validUntil }.
export async function addPasskey({ email, name, label, getToken }) {
  const { options } = await call('/passkey/register/options', { getToken });
  let response;
  try { response = await startRegistration({ optionsJSON: options }); } catch (e) { throw deviceError(e); }
  // replaces: this browser's old passkey, dropped from the list (3.69.3)
  const r = await call('/passkey/register', { getToken, body: { response, label, replaces: passkeyFor(email)?.id || '' } });
  savePasskey(email, { id: r.id, name, validUntil: r.validUntil, paused: false });
  return r;
}

// A password sign-in on this device: 30 more days, and no longer paused
export async function renewPasskey(email, getToken) {
  const p = passkeyFor(email);
  if (!p) return;
  try {
    const r = await call('/passkey/renew', { getToken, body: { id: p.id } });
    savePasskey(email, { validUntil: r.validUntil, paused: false });
  } catch (e) {
    if (e.code === 'PASSKEY_GONE') forgetPasskey(email);   // removed in Security (here or elsewhere)
  }
}

// The lock screen: Face ID → a sign-in token (Firebase custom token)
export async function unlockToken({ email, id }) {
  const { options } = await call('/passkey/unlock/options', { body: { email, id } });
  let response;
  try { response = await startAuthentication({ optionsJSON: options }); } catch (e) { throw deviceError(e, true); }
  const r = await call('/passkey/unlock', { body: { email, id, response } });
  return r.customToken;
}

// 3.74.4: "Sign in with a passkey" on the sign-in page, any device, no email:
// the device offers the person's passkeys for the portal (synced ones too,
// or a phone by QR code). Returns { customToken, email, id, validUntil }.
export function passkeySignInPossible() {
  try { return browserSupportsWebAuthn(); } catch { return false; }
}
export async function passkeySignInToken() {
  const { options, ticket } = await call('/passkey/signin/options');
  let response;
  try { response = await startAuthentication({ optionsJSON: options }); } catch (e) {
    if (cancelled(e)) throw Object.assign(new Error('Passkey sign-in didn’t finish. If your device has no passkey for the Fax Portal, sign in with your password, then turn on Face ID or fingerprint in Security.'), { code: 'CANCELLED' });
    throw deviceError(e);
  }
  return call('/passkey/signin', { body: { ticket, response } });
}

export async function removePasskeys(id, getToken) {
  return call(`/passkey${id ? `/${encodeURIComponent(id)}` : ''}`, { method: 'DELETE', getToken });
}
